import { Graphics,Sprite,Texture } from 'pixi.js';
import { Vector2, EventDispatcher } from 'three';
import { EVENT_SELECTED, EVENT_UNSELECTED, EVENT_KEY_RELEASED, EVENT_KEY_PRESSED,EVENT_DELETED } from '../core/events';
import { KeyboardListener } from './KeyboardManager';
import { Dimensioning } from '../core/dimensioning';
import { selectionColor } from '../core/constants.js';

export class Icons extends Graphics {
    constructor(floorplan, options,icon) {
        super();
        
        this.__eventDispatcher = new EventDispatcher();
        this.__icon = icon;
        this.__iconUrl = this.__icon.url;
        this.__name = this.__icon.name;
        this.__iconSprite = null;
        this.__interactable = true;
        this.__floorplan = floorplan;
        this.__options = options;

        this.__rotation = this.__icon.rotation;
        this.__scale = this.__icon.scale;

        this.interactive = true;
        this.buttonMode = true;
        this.__isDragging = false;
        this.__isSelected = false;
        this.__snapToGrid = false;
        this.__keyboard = new KeyboardListener();
        this.__mouseDownEvent = this.__dragStart.bind(this);
        this.__mouseUpEvent = this.__dragEnd.bind(this);
        this.__mouseMoveEvent = this.__dragMove.bind(this);
        this.__mouseOverEvent = this.__mouseOver.bind(this);
        this.__mouseOutEvent = this.__mouseOut.bind(this);
        this.__mouseClickEvent = this.__click.bind(this);

        this.__borderLine = new Graphics();

        this.__keyListenerEvent = this.__keyListener.bind(this);

        this.__iconDeletedEvent = this.__iconDeleted.bind(this); //this.remove.bind(this);

        this.on('mousedown', this.__mouseClickEvent).on('touchstart', this.__mouseClickEvent);
        this.on('mouseupoutside', this.__mouseUpEvent).on('touchendoutside', this.__mouseUpEvent);
        this.on('mouseup', this.__mouseUpEvent).on('touchend', this.__mouseUpEvent);
        this.on('mousemove', this.__mouseMoveEvent).on('touchmove', this.__mouseMoveEvent);
        this.on('mouseover', this.__mouseOverEvent).on('mouseout', this.__mouseOutEvent);

        this.__icon.addEventListener(EVENT_DELETED, this.__iconDeletedEvent);

        // this.on('click', this.__mouseClickEvent);

        this.__keyboard.addEventListener(EVENT_KEY_RELEASED, this.__keyListenerEvent);
        this.__keyboard.addEventListener(EVENT_KEY_PRESSED, this.__keyListenerEvent);
        this.__update();
        this.__drawHoveredOffState();

    }

    __update(){
        if(this.__iconUrl){
            const texture = Texture.from(this.__iconUrl);
            this.__iconSprite = new Sprite(texture);
            this.__iconSprite.anchor.set(0.5, 0.5);
            this.__iconSprite.pivot.set(0.5,0.5);
            this.__iconSprite.angle = this.__rotation;
            this.__iconSprite.scale.set(this.__scale,this.__scale);
            this.__iconSprite.position = this.__icon.location;
            this.__borderLine.beginFill(selectionColor,0.0);
            this.__borderLine.lineStyle(2, selectionColor);
            this.__borderLine.drawRect(-this.__icon.width / 2,-this.__icon.height / 2, this.__icon.width, this.__icon.height);
            this.__borderLine.endFill();
            this.__iconSprite.addChild(this.__borderLine)
            this.addChild(this.__iconSprite)
        }
    }

    __iconDeleted() {
        this.remove();
        this.__icon = null;
    }

    set iconUrl(value){
        this.__iconUrl = value;
        this.__update();
    }

    get iconUrl(){
        return this.__iconUrl;
    }

    set name(value){
        this.__name = value;
    }

    get name(){
        return this.__name;
    }

    get floorplan(){
        return this.__floorplan;
    }
    
    get icon(){
        return this.__icon;
    }

    set rotation(value){
        this.__rotation = value;
        this.__iconSprite.angle = this.__rotation;
        this.__icon.rotation = this.__rotation;
    }

    get rotation(){
        return this.__rotation;
    }

    set scale(value){
        this.__scale = value;
        this.__iconSprite.scale.set(value,value);
        this.__icon.scale = value;
    }

    get scale(){
        return this.__scale;
    }



    __deactivate() {
        this.off('mousedown', this.__mouseClickEvent).off('touchstart', this.__mouseClickEvent);
        this.off('mouseupoutside', this.__mouseUpEvent).off('touchendoutside', this.__mouseUpEvent);
        this.off('mouseup', this.__mouseUpEvent).off('touchend', this.__mouseUpEvent);
        this.off('mousemove', this.__mouseMoveEvent).off('touchmove', this.__mouseMoveEvent);
        this.off('mouseover', this.__mouseOverEvent).off('mouseout', this.__mouseOutEvent);
    }

    __keyListener(evt) {
        if (this.selected && evt.key === 'Delete') {
            this.__removeFromFloorplan();
        }

        if (evt.type === EVENT_KEY_PRESSED && evt.key === 'Shift') {
            this.__snapToGrid = true;
        }
        if (evt.type === EVENT_KEY_RELEASED && evt.key === 'Shift') {
            this.__snapToGrid = false;
        }
    }

   


    __drawSelectedState() {
        this.__borderLine.alpha = 0.6;
    }
    __drawHoveredOnState() {
        this.__borderLine.alpha = 0.6;
    }
    __drawHoveredOffState() {
        this.__borderLine.alpha = 0.0;
    }

    __click(evt) {
        this.selected = true; 
        this.__isDragging = true;
        this.__dragStart(evt);
        this.__drawSelectedState();
        if (evt !== undefined) {
            evt.stopPropagation();
        }
    }

    __mouseOver(evt) {
        if (!this.selected) {
            this.__drawHoveredOnState();
            evt.stopPropagation();
        }
    }

    __mouseOut(evt) {
        if (!this.selected) {
            this.__drawHoveredOffState();
            if (evt !== undefined) {
                evt.stopPropagation();
            }
        }
    }
    __dragStart(evt) {
        this.__isDragging = true;
        if(evt){
            this.__drawHoveredOffState();
            evt.stopPropagation();
        }        
    }

    __dragEnd(evt) {
        this.__isDragging = false;
        evt.stopPropagation();
    }

    __dragMove(evt) {
        if (this.__isDragging) {
            let co = evt.data.getLocalPosition(this.parent);
            let cmCo = new Vector2(co.x, co.y);
            this.__iconSprite.position = cmCo;
            this.__icon.location = cmCo;
        }
    }
        /**
         * The below method is necessary if the remove event was
         * triggered from within this view class. For example, this view
         * class keeps listening to the delete key pressed. In such a case
         * it is the job of this view class to call the "remove()" method of the
         * associated method. 
         */
    __removeFromFloorplan() {
        //Stub the sub-classes need to implement this functionality
    }

    remove() {
        this.off('mousedown', this.__mouseDownEvent).off('touchstart', this.__mouseDownEvent);
        this.off('mouseupoutside', this.__mouseUpEvent).off('touchendoutside', this.__mouseUpEvent);
        this.off('mouseup', this.__mouseUpEvent).off('touchend', this.__mouseUpEvent);
        this.off('mousemove', this.__mouseMoveEvent).off('touchmove', this.__mouseMoveEvent);
        this.off('mouseover', this.__mouseOverEvent).off('mouseout', this.__mouseOutEvent);
        this.off('click', this.__mouseClickEvent);
        this.__keyboard.removeEventListener(EVENT_KEY_RELEASED, this.__keyListenerEvent);
        this.__keyboard.removeEventListener(EVENT_KEY_PRESSED, this.__keyListenerEvent);
        this.__keyboard.remove();
        if (this.parent) {
            this.parent.removeChild(this);
        }
    }

    addFloorplanListener(type, listener) {
        this.__eventDispatcher.addEventListener(type, listener);
    }

    removeFloorplanListener(type, listener) {
        this.__eventDispatcher.removeEventListener(type, listener);
    }

    get selected() {
        return this.__isSelected;
    }

    set selected(flag) {
        this.__isSelected = flag;
        if (!this.__isSelected) {
            this.__drawHoveredOffState();
        }
        if (flag) {
            this.__eventDispatcher.dispatchEvent({ type: EVENT_SELECTED, item: this });
        } else {
            this.__eventDispatcher.dispatchEvent({ type: EVENT_UNSELECTED, item: this });
        }
    }

    get interactable() {
        return this.__interactable;
    }

    set interactable(flag) {
        this.__interactable = flag;
        this.interactive = flag;
        this.buttonMode = flag;
    }


}